import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ChevronRight,
  ClipboardList,
  Loader2,
  RefreshCw,
  Search,
  Star,
  Trash2,
  Truck,
} from "lucide-react";

import i18next from "i18next";

import {
  reisorProMarketRequest,
} from "../utils/reisorProMarketHttp.js";

const COPY = {
  ru: {
    title: "Мои машины",
    subtitle: "Сохранённые поиски, публикации и парк",
    saved: "Мои сохранённые поиски",
    published: "Мои опубликованные машины",
    park: "Мой парк",
    empty: "Сохранённых поисков машин пока нет.",
    loading: "Загружаем сохранённые поиски…",
    loadError: "Не удалось загрузить сохранённые поиски.",
    open: "Открыть",
    remove: "Удалить",
    removeConfirm: "Удалить сохранённый поиск?",
    removeError: "Не удалось удалить сохранённый поиск.",
    refresh: "Обновить",
    partial: "Догруз",
    tons: "т",
    cubic: "м³",
  },

  uz: {
    title: "Менинг машиналарим",
    subtitle: "Сақланган қидирувлар, эълонлар ва парк",
    saved: "Сақланган машина қидирувларим",
    published: "Эълон қилинган машиналарим",
    park: "Менинг парким",
    empty: "Ҳозирча сақланган машина қидируви йўқ.",
    loading: "Сақланган қидирувлар юкланмоқда…",
    loadError: "Сақланган қидирувларни юклаб бўлмади.",
    open: "Очиш",
    remove: "Ўчириш",
    removeConfirm: "Сақланган қидирув ўчирилсинми?",
    removeError: "Сақланган қидирувни ўчириб бўлмади.",
    refresh: "Янгилаш",
    partial: "Қўшимча юк",
    tons: "т",
    cubic: "м³",
  },

  tr: {
    title: "Araçlarım",
    subtitle: "Kayıtlı aramalar, ilanlar ve filo",
    saved: "Kayıtlı araç aramalarım",
    published: "Yayınladığım araçlar",
    park: "Filom",
    empty: "Henüz kayıtlı araç araması yok.",
    loading: "Kayıtlı aramalar yükleniyor…",
    loadError: "Kayıtlı aramalar yüklenemedi.",
    open: "Aç",
    remove: "Sil",
    removeConfirm: "Kayıtlı arama silinsin mi?",
    removeError: "Kayıtlı arama silinemedi.",
    refresh: "Yenile",
    partial: "Ek yük",
    tons: "t",
    cubic: "m³",
  },
};

function text(value) {
  return String(
    value == null ? "" : value
  ).trim();
}

function languageCode() {
  const raw =
    text(i18next.language)
      .toLowerCase();

  if (raw.startsWith("uz")) {
    return "uz";
  }

  if (raw.startsWith("tr")) {
    return "tr";
  }

  return "ru";
}

function cleanToken(value) {
  return text(value)
    .replace(/^Bearer\s+/i, "");
}

function summary(item, t) {
  const parts = [];

  const from =
    text(item?.from);

  const to =
    text(item?.to);

  if (from || to) {
    parts.push(
      `${from || "—"} → ${to || "—"}`
    );
  }

  if (text(item?.bodyType)) {
    parts.push(
      text(item.bodyType)
    );
  }

  const tons =
    Number(item?.capacityTons);

  if (
    Number.isFinite(tons) &&
    tons > 0
  ) {
    parts.push(
      `${tons} ${t.tons}`
    );
  }

  const volume =
    Number(item?.volumeM3);

  if (
    Number.isFinite(volume) &&
    volume > 0
  ) {
    parts.push(
      `${volume} ${t.cubic}`
    );
  }

  if (item?.partialLoad) {
    parts.push(t.partial);
  }

  return (
    parts.join(" • ") ||
    text(item?.name) ||
    "—"
  );
}

export default function
ReisorProExpeditorVehicleStarHub({
  token = "",
  onOpenSearch,
  onOpenPublished,
  onOpenPark,
}) {
  const lang =
    languageCode();

  const t =
    COPY[lang] ||
    COPY.ru;

  const normalizedToken =
    useMemo(
      () => cleanToken(token),
      [token]
    );

  const [
    items,
    setItems,
  ] = useState([]);

  const [
    busy,
    setBusy,
  ] = useState(false);

  const [
    deletingId,
    setDeletingId,
  ] = useState("");

  const [
    error,
    setError,
  ] = useState("");

  const load =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        if (!normalizedToken) {
          setItems([]);
          setError(t.loadError);
          return;
        }

        if (!silent) {
          setBusy(true);
        }

        setError("");

        try {
          const {
            response,
            data,
          } =
            await reisorProMarketRequest(
              "/vehicle-market/saved-searches",
              {
                token:
                  normalizedToken,
              }
            );

          if (
            !response?.ok ||
            !data?.ok
          ) {
            throw new Error(
              text(data?.error) ||
              "SAVED_VEHICLE_SEARCH_LIST_FAILED"
            );
          }

          setItems(
            Array.isArray(data?.items)
              ? data.items
              : []
          );
        } catch {
          setItems([]);
          setError(t.loadError);
        } finally {
          if (!silent) {
            setBusy(false);
          }
        }
      },
      [
        normalizedToken,
        t.loadError,
      ]
    );

  useEffect(() => {
    void load();
  }, [load]);

  async function
  removeSavedSearch(item) {
    const id =
      text(item?.id);

    if (
      !id ||
      deletingId ||
      !window.confirm(
        t.removeConfirm
      )
    ) {
      return;
    }

    setDeletingId(id);
    setError("");

    try {
      const {
        response,
        data,
      } =
        await reisorProMarketRequest(
          `/vehicle-market/saved-searches/${encodeURIComponent(id)}`,
          {
            method: "DELETE",
            token:
              normalizedToken,
          }
        );

      if (
        !response?.ok ||
        !data?.ok
      ) {
        throw new Error(
          text(data?.error) ||
          "DELETE_FAILED"
        );
      }

      setItems(
        (current) =>
          current.filter(
            (row) =>
              text(row?.id) !== id
          )
      );
    } catch {
      setError(
        t.removeError
      );
    } finally {
      setDeletingId("");
    }
  }

  return (
    <section
      className="space-y-4"
      data-reisor-expeditor-vehicle-star-hub="v1"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Star
              size={23}
              className="fill-amber-400 text-amber-400"
              aria-hidden="true"
            />

            <h1 className="text-2xl font-black text-slate-950 dark:text-white">
              {t.title}
            </h1>
          </div>

          <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
            {t.subtitle}
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            void load()
          }
          disabled={busy}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          aria-label={
            t.refresh
          }
          title={
            t.refresh
          }
        >
          <RefreshCw
            size={19}
            className={
              busy
                ? "animate-spin"
                : ""
            }
          />
        </button>
      </div>

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">
            <Search
              size={21}
              aria-hidden="true"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="font-black text-slate-950 dark:text-white">
              {t.saved}
            </div>

            <div className="text-xs font-bold text-slate-500 dark:text-slate-400">
              {items.length}
            </div>
          </div>
        </div>

        {busy &&
        items.length === 0 ? (
          <div className="flex min-h-[100px] items-center justify-center gap-2 px-4 py-5 text-sm font-bold text-slate-500">
            <Loader2
              size={18}
              className="animate-spin"
            />
            {t.loading}
          </div>
        ) : null}

        {!busy &&
        items.length === 0 ? (
          <div className="px-4 py-5 text-sm font-bold text-slate-500 dark:text-slate-400">
            {t.empty}
          </div>
        ) : null}

        {items.map(
          (item) => {
            const id =
              text(item?.id);

            return (
              <div
                key={id}
                className="border-t border-slate-100 px-4 py-4 first:border-t-0 dark:border-slate-800"
              >
                <div className="font-black text-slate-950 dark:text-white">
                  {text(item?.name) ||
                    summary(
                      item,
                      t
                    )}
                </div>

                <div className="mt-1 break-words text-sm font-medium text-slate-500 dark:text-slate-400">
                  {summary(
                    item,
                    t
                  )}
                </div>

                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      onOpenSearch?.(
                        item
                      )
                    }
                    className="flex min-h-[42px] flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 text-sm font-black text-white"
                  >
                    <Search
                      size={16}
                    />
                    {t.open}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      void removeSavedSearch(
                        item
                      )
                    }
                    disabled={
                      deletingId === id
                    }
                    className="flex min-h-[42px] items-center justify-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 text-sm font-black text-rose-700 disabled:opacity-50 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300"
                  >
                    {deletingId === id ? (
                      <Loader2
                        size={16}
                        className="animate-spin"
                      />
                    ) : (
                      <Trash2
                        size={16}
                      />
                    )}

                    {t.remove}
                  </button>
                </div>
              </div>
            );
          }
        )}

        {error ? (
          <div className="border-t border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
            {error}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={
          onOpenPublished
        }
        className="flex min-h-[72px] w-full items-center gap-4 rounded-3xl border border-slate-200 bg-white px-4 text-left shadow-sm dark:border-slate-800 dark:bg-slate-900"
      >
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
          <Truck
            size={23}
            aria-hidden="true"
          />
        </span>

        <span className="min-w-0 flex-1 font-black text-slate-950 dark:text-white">
          {t.published}
        </span>

        <ChevronRight
          size={20}
          className="text-slate-400"
        />
      </button>

      <button
        type="button"
        onClick={
          onOpenPark
        }
        className="flex min-h-[72px] w-full items-center gap-4 rounded-3xl border border-slate-200 bg-white px-4 text-left shadow-sm dark:border-slate-800 dark:bg-slate-900"
      >
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300">
          <ClipboardList
            size={23}
            aria-hidden="true"
          />
        </span>

        <span className="min-w-0 flex-1 font-black text-slate-950 dark:text-white">
          {t.park}
        </span>

        <ChevronRight
          size={20}
          className="text-slate-400"
        />
      </button>
    </section>
  );
}
